import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { miscApi, productsApi, cmsApi, recommendationsApi } from '@/services/api';
import { getBehaviorPayload, hasAnyBehaviorSignals, trackSearch } from '@/utils/behaviorTracking';
import { useNavCategories } from '@/context/NavCategoriesContext';
import { ProductCard } from '@/features/products/ProductCard';
import { CategoryCard } from '@/components/shared/CategoryCard';
import BannerCarousel from '@/components/shared/BannerCarousel';
import { ArrowRight, Clock, Search, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { buildWebSiteJsonLd, getSiteOrigin, truncateMeta } from '@/utils/seo';
import {
  computeHeroBgStyle,
  computeHeroOverlayStyle,
  DEFAULT_HERO_IMAGE,
  getHeroSubtitleStyle,
  renderColoredTitle,
} from '@/features/home/heroCmsUtils';

// Fallback kategorier om API:et inte svarar
const DEFAULT_CATEGORIES = [
  { id: 'badrum-vvs', name: 'Badrum & VVS', slug: 'badrum-vvs', image_url: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80' },
  { id: 'byggmaterial', name: 'Byggmaterial, trä, fönster & dörrar', slug: 'byggmaterial', image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80' },
  { id: 'el', name: 'El & belysning', slug: 'el', image_url: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=800&q=80' },
  { id: 'farg', name: 'Färg & tapeter', slug: 'farg', image_url: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&q=80' },
  { id: 'golv', name: 'Golv', slug: 'golv', image_url: 'https://images.unsplash.com/photo-1560185893-a55cbc8c57e8?w=800&q=80' },
  { id: 'kakel', name: 'Kakel & klinker', slug: 'kakel', image_url: 'https://images.unsplash.com/photo-1646489553899-c4c4b5a72ccc?w=800&q=80' },
  { id: 'maskiner-verktyg', name: 'Maskiner, verktyg & verkstad', slug: 'maskiner-verktyg', image_url: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&q=80' },
  { id: 'tradgard', name: 'Trädgård', slug: 'tradgard', image_url: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80' },
  { id: 'varme', name: 'Värme & ventilation', slug: 'varme', image_url: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800&q=80' },
  { id: 'smarta-hem', name: 'Smarta Hem', slug: 'smarta-hem', image_url: 'https://images.unsplash.com/photo-1558002038-bb4237b54bed?w=800&q=80' },
  { id: 'arbetsklader', name: 'Arbetskläder', slug: 'arbetsklader', image_url: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=800&q=80' },
  { id: 'arbetsskor', name: 'Arbetsskor', slug: 'arbetsskor', image_url: 'https://images.unsplash.com/photo-1612817288484-6f916006741a?w=800&q=80' },
  { id: 'skyddsutrustning', name: 'Skyddsutrustning', slug: 'skyddsutrustning', image_url: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80' },
];

export const HomePage = () => {
  const { categories: navCats } = useNavCategories();
  const categoryCards = useMemo(() => {
    const mapped = navCats.map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
      image_url: c.image || '',
    }));
    return mapped.length > 0 ? mapped : DEFAULT_CATEGORIES;
  }, [navCats]);

  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [recommendationsArePersonalized, setRecommendationsArePersonalized] = useState(false);
  const [latestProducts, setLatestProducts] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [siteStats, setSiteStats] = useState(null);
  const [loading, setLoading] = useState(true);
  /** När false: visa inte hero-text (undviker flash av inbyggda standardtexter innan CMS svarat). */
  const [cmsHeroReady, setCmsHeroReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        /* Blockera inte startsidan om /seed hänger (t.ex. nätverksproblem) */
        miscApi.seed().catch(() => {});

        cmsApi
          .getContent()
          .then((res) => setSiteContent(res.data))
          .catch(() => setSiteContent(null))
          .finally(() => setCmsHeroReady(true));

        const [latestRes, statsRes] = await Promise.all([
          productsApi.getAll({ limit: 8, sort: 'newest' }),
          miscApi.getPublicStats().catch(() => ({ data: null })),
        ]);

        let recommended = [];
        let personalized = false;
        if (hasAnyBehaviorSignals()) {
          try {
            const recRes = await recommendationsApi.getPersonalized(getBehaviorPayload());
            const list = recRes.data?.products;
            if (Array.isArray(list) && list.length > 0) {
              recommended = list.slice(0, 8);
              personalized = Boolean(recRes.data?.personalized);
            }
          } catch {
            /* use popular below */
          }
        }
        if (recommended.length === 0) {
          const popularRes = await productsApi.getPopular(8);
          recommended = popularRes.data || [];
          personalized = false;
        }

        setRecommendedProducts(recommended);
        setRecommendationsArePersonalized(personalized);
        setLatestProducts(latestRes.data.products || latestRes.data);
        if (statsRes.data) setSiteStats(statsRes.data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      trackSearch(q);
      window.location.href = `/produkter?search=${encodeURIComponent(q)}`;
    }
  };

  // Hero: först när CMS-svar (eller fel) är klart — annars placeholders, aldrig fel "gammal" default i rubriken
  const heroTitle =
    (siteContent?.hero?.title || '').trim() || 'Hitta rätt byggmaterial till rätt pris';
  const heroSubtitle =
    (siteContent?.hero?.subtitle || '').trim() ||
    'Sveriges största marknadsplats för proffs och hemmafixare. Kvalitetsprodukter från pålitliga återförsäljare.';
  const heroBgImage = siteContent?.hero?.background_image || DEFAULT_HERO_IMAGE;

  const heroOverlayStyle = useMemo(
    () => computeHeroOverlayStyle(siteContent?.hero?.brightness),
    [siteContent?.hero?.brightness],
  );

  const heroBgStyle = useMemo(
    () => computeHeroBgStyle(heroBgImage, siteContent?.hero?.background_align_x, siteContent?.hero?.background_align_y),
    [heroBgImage, siteContent?.hero?.background_align_x, siteContent?.hero?.background_align_y],
  );

  const heroSubtitleStyle = useMemo(
    () => getHeroSubtitleStyle(siteContent?.hero?.subtitle_color),
    [siteContent?.hero?.subtitle_color],
  );

  const websiteJsonLd = useMemo(() => buildWebSiteJsonLd(getSiteOrigin()), []);

  // Pre-footer content from CMS
  const preFooter = siteContent?.pre_footer || {};

  return (
    <div data-testid="home-page" className="bg-[#F8FAFC]">
      <SeoMeta
        title={cmsHeroReady ? heroTitle : 'ByggFynd'}
        description={cmsHeroReady ? truncateMeta(heroSubtitle) : 'Byggvaror till bra pris'}
        canonicalPath="/"
        ogImage={heroBgImage}
        jsonLd={websiteJsonLd}
      />
      {/* Premium Hero Section */}
      <section className="hero-premium" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={heroBgStyle} />
        {/* Premium Overlay — ljusstyrka styrs av CMS (admin → Utseende) */}
        <div className="hero-overlay" style={heroOverlayStyle} />
        
        {/* Content */}
        <div className="hero-content animate-slideUp">
          {cmsHeroReady ? (
            <>
              <h1 className="hero-title">
                {renderColoredTitle(heroTitle, siteContent?.hero?.title_colors)}
              </h1>
              <p className="hero-subtitle" style={heroSubtitleStyle}>
                {heroSubtitle}
              </p>
            </>
          ) : (
            <div
              className="w-full max-w-4xl mx-auto space-y-4"
              aria-busy="true"
              aria-label="Laddar rubrik"
            >
              <div className="h-12 sm:h-14 md:h-16 bg-white/15 rounded-lg animate-pulse" />
              <div className="h-5 sm:h-6 bg-white/10 rounded-lg animate-pulse max-w-2xl mx-auto" />
            </div>
          )}
          
          {/* Premium Search Bar */}
          <form onSubmit={handleSearch} className="search-premium" data-testid="hero-search">
            <Search className="h-5 w-5 text-gray-400 ml-4 hidden md:block" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Sök efter produkter, kategorier eller butiker..."
              className="flex-1"
              data-testid="hero-search-input"
            />
            <button type="submit" className="search-btn-premium" data-testid="hero-search-btn">
              <Search className="h-5 w-5" />
              <span className="hidden md:inline">Sök</span>
            </button>
          </form>
          
          {/* Quick stats */}
          <div className="flex items-center justify-center gap-6 md:gap-10 mt-8 text-white/60 text-sm" data-testid="hero-stats">
            <span className="flex items-center gap-2">
              <span className="font-bold text-white text-lg" data-testid="stat-products">{siteStats?.totalProducts || '500+'}</span> produkter
            </span>
            <span className="flex items-center gap-2">
              <span className="font-bold text-white text-lg" data-testid="stat-stores">{siteStats?.totalStores || '50+'}</span> butiker
            </span>
            <span className="flex items-center gap-2">
              <span className="font-bold text-white text-lg" data-testid="stat-categories">{siteStats?.totalCategories || '10'}</span> kategorier
            </span>
          </div>
        </div>
      </section>

      {/* Partner Logos / Banner Carousel */}
      <BannerCarousel />

      {/* Recommended Products */}
      <section className="py-16 md:py-20 bg-white" data-testid="recommended-products-section">
        <div className="container-main">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0B1E33]/5 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-[#0B1E33]" />
              </div>
              <div>
                <h2 className="section-title">Rekommenderat för dig</h2>
                <p className="section-subtitle hidden md:block">Populära produkter baserat på din profil</p>
              </div>
            </div>
            <Link to="/produkter" className="hidden sm:flex items-center gap-2 btn-premium-secondary text-sm py-2 px-5">
              Visa alla <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-lg aspect-square animate-pulse" />
              ))}
            </div>
          ) : recommendedProducts.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {recommendedProducts.slice(0, 8).map((product, index) => (
                <ProductCard key={`recommended-${product.id}-${index}`} product={product} hideShipping />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500">Inga produkter hittades.</p>
            </div>
          )}

          <div className="mt-10 text-center sm:hidden">
            <Link to="/produkter">
              <Button className="btn-premium-secondary">
                Visa alla produkter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Products */}
      <section className="py-16 md:py-20 bg-[#F8FAFC]" data-testid="latest-products-section">
        <div className="container-main">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F97316]/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-[#F97316]" />
              </div>
              <div>
                <h2 className="section-title">Senast inlagda annonser</h2>
                <p className="section-subtitle hidden md:block">De nyaste produkterna på marknadsplatsen</p>
              </div>
            </div>
            <Link to="/produkter?sort=newest" className="hidden sm:flex items-center gap-2 btn-premium-secondary text-sm py-2 px-5">
              Visa alla <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-200 rounded-lg aspect-square animate-pulse" />
              ))}
            </div>
          ) : latestProducts.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {latestProducts.slice(0, 8).map((product) => (
                <ProductCard key={`latest-${product.id}`} product={product} hideShipping />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500">Inga nya annonser.</p>
            </div>
          )}

          <div className="mt-10 text-center sm:hidden">
            <Link to="/produkter?sort=newest">
              <Button className="btn-premium-secondary">
                Visa fler annonser
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories - Premium Scandinavian Design */}
      <section className="py-20 md:py-28 bg-[#FAFBFC]" data-testid="categories-section">
        <div className="container-main">
          {/* Section header with breathing room */}
          <div className="text-center mb-12 md:mb-16">
            <p 
              className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F97316] mb-4"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Produktkategorier
            </p>
            <h2 
              className="text-3xl md:text-4xl text-[#0B1E33] mb-4"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, letterSpacing: '-0.02em' }}
            >
              Utforska vårt sortiment
            </h2>
            <p className="text-slate-500 text-base md:text-lg max-w-xl mx-auto">
              Hitta allt du behöver för ditt nästa byggprojekt
            </p>
          </div>
          
          {/* Category grid with consistent spacing - 5 columns for 10 categories */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {categoryCards.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Pre-footer */}
      {(preFooter.enabled !== false) && (
        <section 
          className="py-20 md:py-24 relative overflow-hidden" 
          style={{ backgroundColor: preFooter.background_color || '#0B1E33' }}
        >
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '32px 32px'
            }} />
          </div>
          
          <div className="container-main text-center relative z-10">
            <h2 
              className="font-heading font-black text-3xl md:text-4xl mb-4"
              style={{ color: preFooter.text_color || '#FFFFFF' }}
            >
              {preFooter.title || 'Vill du sälja byggvaror?'}
            </h2>
            <p 
              className="mb-10 max-w-xl mx-auto text-lg opacity-80"
              style={{ color: preFooter.text_color || '#FFFFFF' }}
            >
              {preFooter.subtitle || 'Registrera dig som företag och börja sälja dina produkter på Sveriges största marknadsplats för byggmaterial.'}
            </p>
            <Link to={preFooter.button_link || '/bli-partner'}>
              <button className="btn-premium-primary text-lg px-10 py-4">
                {preFooter.button_text || 'Skapa företagskonto'}
                <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};
